import { LanguagesEnum } from '@/api/enums';
import { ListingRequest, ListingResponse } from '@/api/types';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import { File } from '@/client/files';
import { Group } from '@/client/groups';
import { LearnerCourseDispatchResponse } from '@/client/learner-course';
import { PlannerType } from '@/client/planner/types';
import { ModifiedContentTranslations, Quiz } from '@/client/quizzes';
import { Role } from '@/client/roles';
import { Tag } from '@/client/tags';
import { User } from '@/client/users';
import { TargetEntitiesRequest, TranslationFunctionType } from '@/common/types';
import { TargetEntitiesFilter } from '@/common/types/targets.type';
import {
  ChapterSlideImg,
  GallerySlideImg,
  StandardSlideImg,
  SummarySlideImg,
} from '@/images/slides';
import {
  SlideAttachmentExcelIcon,
  SlideAttachmentGenericIcon,
  SlideAttachmentPDFIcon,
  SlideAttachmentPowerpointIcon,
  SlideAttachmentWordIcon,
} from '@/utils/helpers/icons.helper';
import React from 'react';

export enum CourseAvailabilityOptionsEnum {
  AUTO = 'auto-available',
  CUSTOM = 'custom',
}

export enum CourseAvailabilityTypeEnum {
  MAKE_AVAILABLE = 'make-available',
  MAKE_UNAVAILABLE = 'make-unavailable',
}

export enum CourseEnrollmentOptionsEnum {
  AUTO = 'auto-enrolled',
  CUSTOM = 'custom',
}

export enum CourseEnrollmentTypeEnum {
  ENROLL = 'enroll',
  UNENROLL = 'unenroll',
}

export enum BranchStatusEnum {
  NOT_SCHEDULED = 'not-scheduled',
  SCHEDULED = 'scheduled',
  ENROLLED = 'enrolled',
}

export enum UserStatusEnum {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  ENROLLED = 'enrolled',
  NOT_SCHEDULED = 'not-scheduled',
  SCHEDULED = 'scheduled',
  UNENROLLED = 'un-enrolled',
}

export enum MaterialType {
  COURSE = 'course',
  RESOURCE = 'template',
  CUSTOM_MATERIAL = 'document',
  POSTER = 'poster',
}

export type CourseTranslationsType = {
  language: string;
  name: string;
  description: string;
};

export type ModifiedCourseTranslationsType = {
  [language: string]: {
    name: string;
    description: string;
  };
};

export type CourseFilesType = {
  id?: string;
  language: string;
  file: File | null;
  fileMeta?: CourseFileMeta;
  priority?: number;
  type?: ContentItemType;
};

export type CourseFileModule = {
  type?: ContentItemType;
  scormDetails?: LearnerCourseDispatchResponse;
} & CourseFilesType;

export type ModifiedCourseFiles = {
  id?: string;
  translations: {
    [language: string]: {
      file: File | null;
      fileMeta?: CourseFileMeta;
      priority?: number;
    };
  };
}[];

type CourseFileMeta = {
  dispatchFile: string;
  remote: string;
};

export enum ContentSeries {
  ESSENTIALS = 'essentials',
  STORIES = 'stories',
}

export const contentSeriesTagColors: Record<ContentSeries, string> = {
  [ContentSeries.ESSENTIALS]: 'blue',
  [ContentSeries.STORIES]: 'blue-dark',
};

export type CourseAccountSchedule = {
  id: string;
  account: { id: string };
  autoEnroll: boolean;
  course: Course;
  scheduledBy: User;
};

export enum CourseStatusAttemptEnum {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  UNENROLLED = 'un-enrolled',
  FAILED = 'failed',
}

export type FilterableCourseAttemptStatus = Extract<
  CourseStatusAttemptEnum,
  CourseStatusAttemptEnum.IN_PROGRESS | CourseStatusAttemptEnum.COMPLETED
>;

export type CourseAttempt = {
  id: string;
  course: Course | null;
  user: User | null;
  status: CourseStatusAttemptEnum;
  created: Date;
  updated: Date;
  payload: Record<string, any>;
  score: number;
  language?: LanguagesEnum;
  completed?: Date;
  quizAttempts?: string[];
};

export enum CourseStatus {
  DRAFT = 'draft',
  SAVED = 'saved',
  PUBLISHED = 'published',
}

export type Course = {
  id: string;
  name: string;
  description: string;
  courseTranslations: CourseTranslationsType[];
  courseLanguages: LanguagesEnum[];
  active: boolean;
  format: CourseFormat;
  courseFiles: CourseFilesType[];
  courseContents?: CourseContent[];
  courseThumbnail: File;
  attempts: CourseAttempt[];
  created: Date;
  updated: Date | null;
  courseCreatedAt: Date;
  courseLastUpdatedAt: Date;
  type: MaterialType;
  retired: boolean;
  selfHosted: boolean;
  isStandardCatalogue: boolean;
  releaseDate: Date | null;
  plannerScheduleDate: Date | null;
  availableToAccounts: Account[];
  account: Account;
  hubspotProperties?: {
    hubspotProperty?: string;
    hubspotDateProperty?: string;
  };
  scheduledAccountsPivot: CourseAccountSchedule[];
  autoEnroll: boolean | null;
  priority?: number | null;
  label?: PlannerType[];
  categories?: PosterCategory[];
  completionPercentage: number | null;
  activeUsersCount: number | null;
  totalNumberOfEnrollments: number | null;
  defaultLanguage: LanguagesEnum;
  skipUsersEnrolledWithinGracePeriod: boolean;
  isPreviewable?: boolean;
  exemptFromMinimumRequirementScore?: boolean;
  status: CourseStatus;
  cloneSettings?: {
    isCloneable?: boolean;
  };
  aiCustomizationSettings?: {
    isEnabled?: boolean;
  };
  cloneMetadata?: {
    clonedAt: string;
    isClone: boolean;
    originalContentId: string;
  };
  customizationMetadata?: {
    originalContentId: string;
    version: number | null;
  };
  tags?: Tag[];
  contentSeries?: ContentSeries;
  hasScormContent?: boolean;
};

export enum PosterCategory {
  COURSE_POSTERS = 'course-posters',
  CYBERSECURITY = 'cybersecurity',
  GDPR = 'gdpr',
  INFO_SCREEN = 'info-screen',
  INFOGRAPHICS = 'infographics',
}

export type ListAccountCoursesRequest = ListingRequest & {
  includeEnrollmentStatistics?: 0 | 1;
  targetSelector?: TargetEntitiesFilter;
};
export type ListCoursesRequest = ListingRequest;
export type ListCoursesResponse = ListingResponse<Course>;

export type CourseFormState = {
  id?: string;
  courseTranslations: ModifiedCourseTranslationsType[];
  courseLanguages?: LanguagesEnum[];
  courseThumbnail?: string;
  format: CourseFormat;
  courseFiles: ModifiedCourseFiles;
  courseContents: CourseContent[];
  courseCreatedAt?: Date;
  courseLastUpdatedAt?: Date;
  type: MaterialType;
  trial?: boolean;
  retired?: boolean;
  customContent?: boolean;
  customAccount?: string | null;
  selfHosted: boolean;
  isStandardCatalogue?: boolean;
  customizationMetadata?: {
    originalContentId: string;
    version: number | null;
  };
  releaseDate: Date | null;
  plannerScheduleDate: Date | null;
  hubspotProperty?: string;
  hubspotDateProperty?: string;
  priority?: number | null;
  label?: PlannerType[];
  categories?: PosterCategory[];
  defaultLanguage?: LanguagesEnum;
  isPreviewable?: boolean;
  exemptFromMinimumRequirementScore?: boolean;
  isCloneable?: boolean;
  isAiCustomizable?: boolean;
  cloneMetadata?: {
    clonedAt: string;
    isClone: boolean;
    originalContentId: string;
  };
  tags?: Tag[];
  contentSeries?: ContentSeries;
};

export type SaveCourseRequest = {
  courseTranslations: CourseTranslationsType[];
  courseLanguages?: LanguagesEnum[];
  courseThumbnail?: string | null;
  format: CourseFormat;
  courseFiles: CourseFilesType[];
  courseContents?: CourseContent[];
  courseCreatedAt?: Date;
  courseLastUpdatedAt?: Date;
  type?: MaterialType;
  trial?: boolean;
  isStandardCatalogue?: boolean;
  releaseDate: Date | null;
  plannerScheduleDate: Date | null;
  account: string | null;
  hubspotProperty?: string;
  hubspotDateProperty?: string;
  priority?: number | null;
  label?: PlannerType[];
  categories?: PosterCategory[];
  isPreviewable?: boolean;
  exemptFromMinimumRequirementScore?: boolean;
  isCloneable?: boolean;
  isAiCustomizable?: boolean;
  status?: CourseStatus;
  tags?: string[];
  contentSeries?: ContentSeries;
};

export type UpdateCourse = {
  courseTranslations?: CourseTranslationsType[];
  courseLanguages?: LanguagesEnum[];
  courseThumbnail?: string | null;
  format: CourseFormat;
  courseFiles?: CourseFilesType[];
  courseContents?: CourseContent[];
  courseCreatedAt?: Date;
  courseLastUpdatedAt?: Date;
  type?: MaterialType;
  trial?: boolean;
  isStandardCatalogue?: boolean;
  releaseDate?: Date | null;
  plannerScheduleDate?: Date | null;
  account?: string | null;
  selfHosted?: boolean;
  retired?: boolean;
  hubspotProperty?: string;
  hubspotDateProperty?: string;
  priority?: number | null;
  label?: PlannerType[];
  categories?: PosterCategory[];
  isPreviewable?: boolean;
  exemptFromMinimumRequirementScore?: boolean;
  isCloneable?: boolean;
  isAiCustomizable?: boolean;
  status?: CourseStatus;
  tags?: string[];
  contentSeries?: ContentSeries;
};

export type UpdateCourseRequest = {
  updates: UpdateCourse;
  courseId: string;
};

export type CourseAvailabilityFormValues = {
  accounts: Account[];
};

export type MakeAvailableAccountsRequest = {
  courseId: string;
  accounts: string[];
};

export type MakeAvailableCoursesToAccountRequest = {
  courses: string[];
  accountId: string;
};

export type MakeAvailableCoursesToAccountFormValues = {
  courses: Course[];
  isCatalogueEnabled: boolean;
  isPosterCatalogueEnabled: boolean;
};

// Course Schedule

export enum CourseEntityScheduleEnum {
  USER = 'user',
  BRANCH = 'branch',
  GROUP = 'group',
  ACCOUNT = 'account',
}

export enum CourseEntityEnrollEnum {
  USERS = 'users',
  BRANCHES = 'branches',
  GROUPS = 'groups',
  ACCOUNTS = 'accounts',
}

export const mapScheduleEntityEnumToEnrollEnum = {
  [CourseEntityScheduleEnum.USER]: CourseEntityEnrollEnum.USERS,
  [CourseEntityScheduleEnum.GROUP]: CourseEntityEnrollEnum.GROUPS,
  [CourseEntityScheduleEnum.BRANCH]: CourseEntityEnrollEnum.BRANCHES,
  [CourseEntityScheduleEnum.ACCOUNT]: CourseEntityEnrollEnum.ACCOUNTS,
};

export enum CourseScheduleType {
  STATIC = 'static',
  DYNAMIC = 'dynamic',
}

export type CourseEntity = {
  id: string;
  name: string;
  thumbnailUrl?: string;
  releaseDate: Date | null;
};

export type CourseSchedule = {
  account?: { id: string; name: string };
  course?: CourseEntity;
  id: string;
  date: Date;
  unEnrolledAt: Date;
  dueDate: Date | null;
  executedAt: Date;
  autoEnroll: boolean;
  entity: { id: string; name: string; type: CourseEntityScheduleEnum };
  scheduledBy: { id: string; name: string };
  usersCount: number;
  type: CourseScheduleType;
};

export type CourseAccount = {
  available: boolean;
  branchesCount: number;
  groupsCount: number;
  usersCount: number;
  enrolledBranchesCount: number;
  enrolledGroupsCount: number;
  enrolledUsersCount: number;
  id: string;
  name: string;
  schedule: {
    id: string;
    date: Date | null;
    executedAt: Date | null;
    autoEnroll: boolean;
  };
};

export type CourseBranch = {
  enrolledUsersCount: number;
  id: string;
  name: string;
  usersCount: number;
  schedule: {
    id: string;
    date: Date | null;
    executedAt: Date | null;
    autoEnroll: boolean;
  } | null;
  enrollment: {
    enrollmentDate: Date;
    source: CourseEntityScheduleEnum;
  } | null;
  status: BranchStatusEnum;
  enrollDate: Date;
  inheritedSchedule: {
    date: Date;
    source: CourseEntityScheduleEnum;
  } | null;
};

export type CourseUser = {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: Role;
  schedule: {
    id: string;
    date: Date | null;
    source: CourseEntityScheduleEnum;
    executedAt: Date | null;
  } | null;
  enrollment: {
    id: string;
    dueDate: Date | null;
    enrollmentDate: Date;
    unEnrolledAt: Date | null;
    unEnrolledBy?: { id: string; email: string };
    source: CourseEntityScheduleEnum;
  } | null;
  status: UserStatusEnum;
  enrollDate: Date;
};

export type BaseEnrollFormValues = {
  courses: Course[];
  date: Date | null;
  type: CourseScheduleType;
  autoEnroll?: boolean;
};

export type AccountsEnrollFormValues = {
  accounts: Account[];
} & BaseEnrollFormValues;

export type EnrollFormValues = {
  accounts?: Account[];
  branches?: Branch[];
  groups?: Group[];
  users?: User[];
} & BaseEnrollFormValues;

export type EnrollManyToManyFormValues = {
  enrollTo: CourseEntityScheduleEnum;
} & EnrollFormValues;

type BaseScheduleRequest = {
  courses: string[];
  date: Date | null;
  autoEnroll?: boolean;
  type: CourseScheduleType;
};

export type AccountsSchedulesRequest = {
  accounts: string[];
} & BaseScheduleRequest;

export type BranchesSchedulesRequest = {
  branches: string[];
} & BaseScheduleRequest;

export type GroupsSchedulesRequest = {
  groups: string[];
} & BaseScheduleRequest;

export type UsersSchedulesRequest = {
  users: string[];
} & BaseScheduleRequest;

export type ChangeEnrollDateFormValues = {
  date: Date | undefined;
  type: CourseScheduleType;
};

export type ChangeScheduleDateRequest = {
  scheduleId: string;
  scheduleType: CourseEntityScheduleEnum;
  date: Date;
  type: CourseScheduleType;
};

export type ChangeScheduleEnrollTypeRequest = {
  scheduleId: string;
  scheduleType: CourseEntityScheduleEnum;
  autoEnroll?: boolean;
  type: CourseScheduleType;
};

export type DeleteScheduleRequest = {
  entity: any;
  id: string;
  source: CourseEntityScheduleEnum;
};

export type DeleteEnrollmentRequest = {
  accounts?: string[];
  branches?: string[];
  groups?: string[];
  users?: string[];
};

export type DeleteEnrollmentPayload = {
  courseId: string;
  request: DeleteEnrollmentRequest;
};

export type DeleteEnrollmentFormValues = {
  courseId: string;
  entity: CourseEntityEnrollEnum;
  value: Account[] | Branch[] | Group[] | User[];
};

export type ForceCompleteFormValues = { score: number };

export type ListCourseScheduleRequest = ListingRequest;
export type ListCourseScheduleResponse = ListingResponse<CourseSchedule>;

export type ListAccountScheduleRequest = ListingRequest;
export type ListAccountScheduleResponse = ListingResponse<CourseSchedule>;

export type ListCourseAccountRequest = ListingRequest;
export type ListCourseAccountResponse = ListingResponse<CourseAccount>;

export type ListCourseBranchesRequest = ListingRequest;
export type ListCourseBranchesResponse = ListingResponse<CourseBranch>;

export type ListCourseGroupsRequest = ListingRequest;
export type ListCourseGroupsResponse = ListingResponse<CourseBranch>;

export type ListCourseUsersRequest = ListingRequest;
export type ListCourseUsersResponse = ListingResponse<CourseUser>;

export type ForceCompleteRequest = {
  courseId: string;
  userId: string;
  score: number;
  accountId: string;
};

export type MigrateCourseResponse = {
  enrollmentsUpdated: number;
  attemptsUpdated: number;
  accountAvailabilitiesUpdated: number;
  accountSchedulesUpdated: number;
  groupSchedulesUpdated: number;
  branchSchedulesUpdated: number;
  userSchedulesUpdated: number;
  quizAttemptsUpdated: number;
  success: boolean;
};

export enum MigrateCourseMode {
  EVERYTHING = 'everything',
  FEEDBACK_ONLY = 'feedback-only',
}

export type HighlightedCourse = {
  id: string;
  name: string;
  description: string | null;
  releaseDate: Date | null;
  thumbnailSignedUrl: string | null;
  highlight: {
    isEnabledForRegularUser: boolean | null;
    isEnabledForTrialUser: boolean | null;
    regularUserOrder: number | null;
    trialUserOrder: number | null;
  };
};

export enum CourseFormat {
  FILES = 'files',
  CONTENTS = 'contents',
}

export enum ContentItemType {
  VIDEO = 'video',
  SCORM = 'scorm',
  PDF = 'pdf',
  QUIZ = 'quiz',
  FEEDBACK = 'feedback',
  PRESENTATION = 'presentation',
}

export enum CourseContentType {
  RESOURCE = 'resource',
  QUIZ = 'quiz',
  PRESENTATION = 'presentation',
}

export type CourseContent = {
  id?: string;
  position: number;
  title?: string;
  type: CourseContentType | ContentItemType;
  quiz?: Quiz;
  resource?: Resource;
  presentation?: SlideDeck;
  isAutoNamed?: boolean;
};

export type ResourceTranslation = {
  id: string;
  language: LanguagesEnum;
  file: File;
  fileMeta?: CourseFileMeta;
};

export type ResourceTranslationPayload = {
  language: LanguagesEnum | string;
  file: string | File | null | undefined;
  fileMeta?: CourseFileMeta;
};

export enum ResourceType {
  VIDEO = 'video',
  PDF = 'pdf',
  SCORM = 'scorm',
  WORD = 'word',
  POWERPOINT = 'powerpoint',
  EXCEL = 'excel',
}

export type Resource = {
  id?: string;
  title: string;
  type: ResourceType;
  translations: ResourceTranslation[];
  supportedLanguages?: LanguagesEnum[];
  fullyTranslatedLanguages?: LanguagesEnum[];
  fallbackLanguage?: LanguagesEnum | null;
  requiresReadConfirmation?: boolean;
};

export type ModifiedResource = Resource & {
  translations: ModifiedContentTranslations;
};

export type CourseUsersSummaryRequest = {
  enrollDate: string;
  targets?: TargetEntitiesRequest;
  excludes?: TargetEntitiesRequest;
};

export type CourseUsersSummary = {
  reEnrollableUsersCount: number;
  enrolledUsersCount: number;
  totalUsersCount: number;
};

export enum SlideType {
  NORMAL = 'normal',
  POPUP = 'popup',
}

export enum SlideTemplateType {
  TEXT_IMAGE = 'text-image',
  CHAPTER = 'chapter',
  GALLERY = 'gallery',
  SUMMARY = 'summary',
}

export enum InteractiveElementType {
  EMAIL = 'email',
  SMS = 'sms',
}

export type InteractiveElement = {
  type?: InteractiveElementType;
  data?: any; // any type of data can be stored here
};

export type EmailAttachment = {
  type: string;
  id: string;
  label?: string;
};

export const attachmentTypeOptions = (t: TranslationFunctionType) => [
  {
    label: t('content.email.attachment.pdf'),
    value: 'pdf',
    icon: React.createElement(SlideAttachmentPDFIcon),
  },
  {
    label: t('content.email.attachment.word'),
    value: 'word',
    icon: React.createElement(SlideAttachmentWordIcon),
  },
  {
    label: t('content.email.attachment.powerpoint'),
    value: 'powerpoint',
    icon: React.createElement(SlideAttachmentPowerpointIcon),
  },
  {
    label: t('content.email.attachment.excel'),
    value: 'excel',
    icon: React.createElement(SlideAttachmentExcelIcon),
  },
  {
    label: t('content.email.attachment.generic'),
    value: 'generic',
    icon: React.createElement(SlideAttachmentGenericIcon),
  },
];

export const SlideTemplateOptions = (t: TranslationFunctionType) => [
  {
    label: t(`content.slide.template.${SlideTemplateType.TEXT_IMAGE}`),
    value: SlideTemplateType.TEXT_IMAGE,
    image: StandardSlideImg,
  },
  {
    label: t(`content.slide.template.${SlideTemplateType.CHAPTER}`),
    value: SlideTemplateType.CHAPTER,
    image: ChapterSlideImg,
  },

  {
    label: t(`content.slide.template.${SlideTemplateType.GALLERY}`),
    value: SlideTemplateType.GALLERY,
    image: GallerySlideImg,
  },
  {
    label: t(`content.slide.template.${SlideTemplateType.SUMMARY}`),
    value: SlideTemplateType.SUMMARY,
    image: SummarySlideImg,
  },
];

export type Slide = {
  id?: string;
  type?: SlideType;
  templateType: SlideTemplateType;
  position?: number;
  infobox?: string;
  headline?: string;
  html?: string;
  footnote?: string;
  images?: SlideImage[];
  popupSlide?: Slide | string;
  tempPopupSlideId?: string;
  tempId?: string;
  textAlignment?: TextAlignment;
  interactiveElement?: InteractiveElement | null;
  shouldSkipImages?: boolean;
  isAiCustomizationEnabled?: boolean;
  aiCustomizationInstructions?: string;
};

export type SlideDeck = {
  id?: string;
  title: string;
  description?: string;
  translations: { language: LanguagesEnum; slides: Slide[] }[];
  fullyTranslatedLanguages?: LanguagesEnum[];
};

export type SlideImage = {
  file: File | null;
  position: number;
  withBorder?: boolean;
  withZoom?: boolean;
  title?: string;
  description?: string;
};

export enum TextAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}
